export * from "./extension";

import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { jobBoardSchema } from "../job-search.dto";

// Spec 033 — extension claim/result/answer-question/status DTOs.
// Re-exported here so consumers can import either via the extension barrel
// or via the top-level auto-apply barrel.
export * from "./extension";

// ---------------------------------------------------------------------------
// Spec 031 — per-user board toggles + launch banner.
// ---------------------------------------------------------------------------

/** Read response from GET /api/me/board-preferences. */
export const boardPreferencesResponseSchema = z.object({
  visibleBoards: z.array(jobBoardSchema),
  preferences: z.record(jobBoardSchema, z.boolean()),
  // Pre-launch — per-board auto-apply preferences (separate axis from
  // discovery `preferences`). Only boards with a submission worker
  // appear here.
  autoApply: z.record(jobBoardSchema, z.boolean()).default({}),
  submittableBoards: z.array(jobBoardSchema).default([]),
});

export class BoardPreferencesResponseDto extends createZodDto(boardPreferencesResponseSchema) {}

/** Body for PUT /api/me/board-preferences — partial update. */
export const updateBoardPreferencesSchema = z.object({
  preferences: z
    .record(jobBoardSchema, z.boolean())
    .refine((obj) => Object.keys(obj).length > 0, "preferences must include at least one board"),
});

export class UpdateBoardPreferencesDto extends createZodDto(updateBoardPreferencesSchema) {}

/** Response from POST /api/me/banner-dismiss. */
export const bannerDismissResponseSchema = z.object({
  dismissedAt: z.string().datetime(),
});

export class BannerDismissResponseDto extends createZodDto(bannerDismissResponseSchema) {}

// Spec 030 adds VALIDATING for in-flight credential checks (FR-006 retry flow).
export const credentialStatusSchema = z.enum(["CONNECTED", "EXPIRED", "PAUSED", "VALIDATING"]);

export class ConnectLinkedInDto extends createZodDto(
  z.object({
    platform: z.literal("LINKEDIN"),
    sessionCookie: z.string().trim().min(10),
  }),
) {}

export class ConnectIndeedDto extends createZodDto(
  z.object({
    platform: z.literal("INDEED"),
    email: z.string().email(),
    password: z.string().min(1),
  }),
) {}

export const connectCredentialSchema = z.discriminatedUnion("platform", [
  z.object({
    platform: z.literal("LINKEDIN"),
    sessionCookie: z.string().trim().min(10),
  }),
  z.object({
    platform: z.literal("INDEED"),
    email: z.string().email(),
    password: z.string().min(1),
  }),
]);

export const credentialFailureReasonSchema = z.enum([
  "INVALID_VALUE",
  "EXPIRED_COOKIE",
  "CHECKPOINT_REQUIRED",
  "UPSTREAM_FAILURE",
  "VALIDATION_TIMEOUT",
  "SERVICE_UNAVAILABLE",
  "RATE_LIMITED",
]);

export type CredentialFailureReason = z.infer<typeof credentialFailureReasonSchema>;

export const testCredentialResponseSchema = z.object({
  platform: jobBoardSchema,
  valid: z.boolean(),
  failureReason: credentialFailureReasonSchema.nullable().default(null),
  message: z.string().nullable().default(null),
  correlationId: z.string().uuid(),
});

export type TestCredentialResponseDto = z.infer<typeof testCredentialResponseSchema>;

export const connectCredentialDtoSchema = z
  .object({
    platform: jobBoardSchema,
    sessionCookie: z.string().trim().min(10).optional(),
    email: z.string().email().optional(),
    password: z.string().min(1).optional(),
  })
  .superRefine((value, context) => {
    if (value.platform === "LINKEDIN" && !value.sessionCookie) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sessionCookie"],
        message: "Session cookie is required for LinkedIn connections.",
      });
    }

    if (value.platform === "INDEED") {
      if (!value.email) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["email"],
          message: "Email is required for Indeed connections.",
        });
      }

      if (!value.password) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["password"],
          message: "Password is required for Indeed connections.",
        });
      }
    }
  });

export class ConnectCredentialDto extends createZodDto(connectCredentialDtoSchema) {}

// `createZodDto` cannot wrap a discriminated union (TS2509). Reuse the flat
// schema with `superRefine` that already powers ConnectCredentialDto.
export const testCredentialDtoSchema = connectCredentialDtoSchema;

export class TestCredentialDto extends createZodDto(testCredentialDtoSchema) {}

export class KillSwitchDto extends createZodDto(
  z.object({
    enabled: z.boolean(),
  }),
) {}

const ianaTimezoneSchema = z
  .string()
  .min(1)
  .refine(
    (tz) => {
      try {
        Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: "Must be a valid IANA timezone string." },
  );

const scheduleSchema = z.array(z.array(z.boolean()).length(24)).length(7).optional();

export const updateAutoApplySettingsDtoSchema = z.object({
  dailyLimit: z.coerce
    .number()
    .int()
    .refine((value) => value === -1 || (value >= 1 && value <= 50), {
      message: "Daily limit must be -1 (unlimited) or a value between 1 and 50.",
    })
    .optional(),
  matchThreshold: z.number().int().min(40).max(100).optional(),
  autoSubmitOnTimeout: z.boolean().optional(),
  showPreview: z.boolean().optional(),
  holdBorderline: z.boolean().optional(),
  aiCoverLetter: z.boolean().optional(),
  schedule: scheduleSchema,
  timezone: ianaTimezoneSchema.optional(),
  hardRules: z
    .object({
      blockedCompanies: z.array(z.string()).optional(),
      dedup90d: z.boolean().optional(),
      coverLetterApproval: z.boolean().optional(),
      skipNoVisa: z.boolean().optional(),
    })
    .optional(),
  sources: z
    .object({
      linkedinEasyApply: z.boolean().optional(),
      lever: z.boolean().optional(),
      googleJobs: z.boolean().optional(),
    })
    .optional(),
  advancedFilters: z
    .object({
      visaSponsorshipOnly: z.boolean().optional(),
      remoteOnly: z.boolean().optional(),
      salaryFloor: z.number().nullable().optional(),
    })
    .optional(),
});

export class UpdateAutoApplySettingsDto extends createZodDto(updateAutoApplySettingsDtoSchema) {}

export class NotificationListQueryDto extends createZodDto(
  z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    unreadOnly: z.coerce.boolean().default(false),
  }),
) {}

export const credentialListItemSchema = z.object({
  platform: jobBoardSchema,
  status: credentialStatusSchema,
  lastUsedAt: z.coerce.date().nullable().default(null),
  lastValidatedAt: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
});

export type CredentialListItemDto = z.infer<typeof credentialListItemSchema>;

export const credentialConnectResponseSchema = credentialListItemSchema.pick({
  platform: true,
  status: true,
  lastValidatedAt: true,
});

export type CredentialConnectResponseDto = z.infer<typeof credentialConnectResponseSchema>;

export const credentialDisconnectResponseSchema = z.object({
  platform: jobBoardSchema,
  status: z.literal("DISCONNECTED"),
});

export type CredentialDisconnectResponseDto = z.infer<typeof credentialDisconnectResponseSchema>;

export const credentialValidationResponseSchema = z.object({
  platform: jobBoardSchema,
  valid: z.boolean(),
  lastValidatedAt: z.coerce.date().nullable().default(null),
});

export type CredentialValidationResponseDto = z.infer<typeof credentialValidationResponseSchema>;

export const notificationTypeSchema = z.enum([
  "SUBMISSION_SUCCESS",
  "SUBMISSION_FAILED",
  "CAPTCHA_DETECTED",
  "ACCOUNT_RESTRICTED",
  "DAILY_LIMIT_REACHED",
  "CREDENTIAL_EXPIRED",
  "AUTOMATION_PAUSED",
  "AUTOMATION_RESUMED",
]);

export const notificationSchema = z.object({
  id: z.string(),
  type: notificationTypeSchema,
  title: z.string(),
  message: z.string().nullable().default(null),
  metadata: z.record(z.unknown()).nullable().default(null),
  readAt: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
});

export type NotificationDto = z.infer<typeof notificationSchema>;

export const paginatedNotificationsSchema = z.object({
  items: z.array(notificationSchema),
  meta: z.object({
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
    unreadCount: z.number().int(),
  }),
});

export type PaginatedNotificationsDto = z.infer<typeof paginatedNotificationsSchema>;

export const unreadCountSchema = z.object({
  count: z.number().int(),
});

export type UnreadCountDto = z.infer<typeof unreadCountSchema>;

export const markNotificationReadSchema = z.object({
  id: z.string(),
  readAt: z.coerce.date(),
});

export type MarkNotificationReadDto = z.infer<typeof markNotificationReadSchema>;

export const markAllNotificationsReadSchema = z.object({
  markedCount: z.number().int(),
});

export type MarkAllNotificationsReadDto = z.infer<typeof markAllNotificationsReadSchema>;

export const autoApplyRateWindowSchema = z.object({
  count: z.number().int(),
  limit: z.number().int(),
});

export const autoApplySettingsSchema = z.object({
  applicationsUsedToday: z.number().int(),
  applicationsLimitToday: z.number().int(),
  rateStatus: z.object({
    linkedin: autoApplyRateWindowSchema,
    indeed: autoApplyRateWindowSchema,
  }),
});

export type AutoApplySettingsDto = z.infer<typeof autoApplySettingsSchema>;

export const killSwitchStateSchema = z.object({
  key: z.literal("automation.killSwitch").default("automation.killSwitch"),
  enabled: z.boolean(),
  updatedAt: z.coerce.date(),
});

export type KillSwitchStateDto = z.infer<typeof killSwitchStateSchema>;
